import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { ChatConversation } from '@/types/chat.types'
import { Search, Bell, BellOff, Settings, Users } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { supabase } from '@/integrations/supabase/client'
import { useNotifications } from '@/hooks/useNotifications'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { AgentAvatar } from './AgentAvatar'
import { AgentStatusIndicator } from '@/components/agent-status/AgentStatusIndicator'
import { useTranslation } from '@/hooks/useTranslation'

interface ConversationListProps {
	programId: string
	selectedConversationId: string | null
	onSelectConversation: (id: string) => void
}

export function ConversationList({
	programId,
	selectedConversationId,
	onSelectConversation,
}: ConversationListProps) {
	const { t } = useTranslation()
	const [searchTerm, setSearchTerm] = useState('')
	const [statusFilter, setStatusFilter] = useState<'open' | 'closed' | 'all'>(
		'open'
	)
	const [newConversationIds, setNewConversationIds] = useState<Set<string>>(
		new Set()
	)
	const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({})

	const queryClient = useQueryClient()
	const {
		preferences,
		updatePreferences,
		isUpdatingPreferences,
		requestPermission,
		permission,
		playSound,
	} = useNotifications()

	const {
		data: conversations = [],
		isLoading,
		error,
	} = useQuery<ChatConversation[], Error>({
		queryKey: ['conversations', programId],
		queryFn: () => apiClient(`/api/chat/conversations?programId=${programId}`),
		enabled: !!programId,
	})

	const getCompactTime = (dateString: string, t: (key: string) => string): string => {
		const now = Date.now()
		const date = new Date(dateString).getTime()
		const diffMs = now - date
		const diffMins = Math.floor(diffMs / 60000)

		if (diffMins < 1) return t('conversationList.now')
		if (diffMins < 60) return `${diffMins}${t('conversationList.m')}`

		const diffHours = Math.floor(diffMins / 60)
		if (diffHours < 24) return `${diffHours}${t('conversationList.h')}`

		const diffDays = Math.floor(diffHours / 24)
		if (diffDays < 7) return `${diffDays}${t('conversationList.d')}`

		const diffWeeks = Math.floor(diffDays / 7)
		return `${diffWeeks}${t('conversationList.w')}`
	}

	// Subscribe to real-time updates
	useEffect(() => {
		if (!programId) return

		const channel = supabase
			.channel(`conversations:${programId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'chat_conversations',
					filter: `program_id=eq.${programId}`,
				},
				(payload) => {
					const newConvo = payload.new as ChatConversation

					setNewConversationIds((prev) => new Set(prev).add(newConvo.id))

					setTimeout(() => {
						setNewConversationIds((prev) => {
							const updated = new Set(prev)
							updated.delete(newConvo.id)
							return updated
						})
					}, 10000)

					queryClient.invalidateQueries({
						queryKey: ['conversations', programId],
					})

					try {
						const audio = new Audio(
							'data:audio/wav;base64,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'
						)
						audio.volume = 0.3
						audio.play().catch(() => {})
					} catch (e) {
						// Silent fail
					}
				}
			)
			.on(
				'postgres_changes',
				{
					event: 'UPDATE',
					schema: 'public',
					table: 'chat_conversations',
					filter: `program_id=eq.${programId}`,
				},
				(payload) => {
					queryClient.invalidateQueries({
						queryKey: ['conversations', programId],
					})
				}
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [programId, queryClient])

	const filteredConversations = conversations.filter((convo) => {
		const matchesSearch =
			convo.visitor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			convo.visitor_email?.toLowerCase().includes(searchTerm.toLowerCase())

		const matchesStatus =
			statusFilter === 'all'
				? true
				: statusFilter === 'open'
				? convo.status === 'open'
				: convo.status === 'closed'

		return matchesSearch && matchesStatus
	})

	return (
		<div className="flex flex-col h-full bg-background border-r border-border/60">
			{/* Header */}
			<div className="px-4 py-4 border-b border-border/60 bg-card/30">
				<div className="flex items-center justify-between mb-4 gap-2">
					<div className="flex items-center gap-2 min-w-0">
						<h1 className="text-xl md:text-2xl font-semibold text-foreground tracking-tight">
							{t('conversationList.liveChat')}
						</h1>
						<AgentStatusIndicator programId={programId} />
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<div className="flex items-center gap-2 px-2 py-1 bg-accent/10 rounded-md">
							<div className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse"></div>
							<span className="text-xs font-medium text-accent">
								{conversations.filter((c) => c.status === 'open').length} {t('conversationList.active')}
							</span>
						</div>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-10 w-10 rounded-md hover:bg-muted/60 transition-colors duration-150"
									aria-label="Notification settings">
									{permission === 'granted' && preferences?.desktop_enabled ? (
										<Bell className="h-4 w-4 text-accent" />
									) : (
										<BellOff className="h-4 w-4 text-muted-foreground" />
									)}
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-72">
								<DropdownMenuLabel>{t('conversationList.notificationSettings')}</DropdownMenuLabel>
								<DropdownMenuSeparator />

								{permission !== 'granted' ? (
									<DropdownMenuItem onClick={requestPermission}>
										<Bell className="mr-2 h-4 w-4" />
										{t('conversationList.enableBrowserNotifications')}
									</DropdownMenuItem>
								) : (
									<>
										<div className="px-2 py-2">
											<div className="flex items-center justify-between">
												<label
													htmlFor="desktop-notif"
													className="text-sm font-medium flex items-center gap-2">
													<Bell className="h-4 w-4" />
													{t('conversationList.desktopAlerts')}
												</label>
												<Switch
													id="desktop-notif"
													checked={preferences?.desktop_enabled || false}
													disabled={isUpdatingPreferences}
													onCheckedChange={(checked) =>
														updatePreferences({ desktop_enabled: checked })
													}
												/>
											</div>
										</div>

										<div className="px-2 py-2">
											<div className="flex items-center justify-between">
												<label
													htmlFor="sound-notif"
													className="text-sm font-medium flex items-center gap-2">
													<Settings className="h-4 w-4" />
													{t('conversationList.soundAlerts')}
												</label>
												<Switch
													id="sound-notif"
													checked={preferences?.sound_enabled || false}
													disabled={isUpdatingPreferences}
													onCheckedChange={(checked) =>
														updatePreferences({ sound_enabled: checked })
													}
												/>
											</div>
										</div>
									</>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>

				{/* Status Filter Tabs */}
				<div className="flex gap-2 mb-3">
					<button
						className={cn(
							"px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
							statusFilter === 'all'
								? "bg-primary text-primary-foreground"
								: "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
						)}
						onClick={() => setStatusFilter('all')}
						aria-label={t('conversationList.showAllConversations')}
						aria-pressed={statusFilter === 'all'}
					>
						{t('conversationList.all')}
					</button>
					<button
						className={cn(
							"px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
							statusFilter === 'open'
								? "bg-primary text-primary-foreground"
								: "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
						)}
						onClick={() => setStatusFilter('open')}
						aria-label={t('conversationList.showOpenConversations')}
						aria-pressed={statusFilter === 'open'}
					>
						{t('conversationList.open')}
					</button>
					<button
						className={cn(
							"px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
							statusFilter === 'closed'
								? "bg-primary text-primary-foreground"
								: "bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground"
						)}
						onClick={() => setStatusFilter('closed')}
						aria-label={t('conversationList.showClosedConversations')}
						aria-pressed={statusFilter === 'closed'}
					>
						{t('conversationList.closed')}
					</button>
				</div>

				<div className="relative">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
					<Input
						placeholder={t('conversationList.searchByNameOrEmail')}
						className="pl-10 h-9 bg-muted/30 border-border/40 rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary/50 transition-all duration-150 placeholder:text-muted-foreground/60 text-sm"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						aria-label={t('conversationList.searchConversations')}
					/>
				</div>
			</div>

			{/* Conversations List */}
			<div className="flex-1 overflow-y-auto overflow-x-hidden">
				{isLoading ? (
					<div className="px-2 py-2 space-y-2">
						{[...Array(4)].map((_, i) => (
							<div
								key={i}
								className="p-3 rounded-lg border border-border">
								<div className="flex gap-3">
									<Skeleton className="h-11 w-11 rounded-full flex-shrink-0" />
									<div className="flex-1 min-w-0 space-y-2">
										<div className="flex items-center justify-between">
											<Skeleton className="h-4 w-20" />
											<Skeleton className="h-3 w-10" />
										</div>
										<Skeleton className="h-3 w-full" />
										<Skeleton className="h-3 w-16" />
									</div>
								</div>
							</div>
						))}
					</div>
				) : error ? (
					<div className="px-4 py-8 text-center">
						<p className="text-sm text-destructive">{error.message}</p>
					</div>
				) : filteredConversations.length === 0 ? (
					<div className="px-4 py-8 text-center space-y-3">
						<div className="w-12 h-12 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-3">
							<Users className="h-5 w-5 text-muted-foreground/60" />
						</div>
						<h3 className="font-medium text-foreground text-sm">
							{t('conversationList.noConversationsFound')}
						</h3>
						<p className="text-sm text-muted-foreground">
							{searchTerm
								? t('conversationList.tryAdjustingSearch')
								: t('conversationList.newConversationsWillAppear')}
						</p>
					</div>
				) : (
					<div className="px-2 py-2 space-y-2">
						{filteredConversations.map((convo) => {
							const isSelected = selectedConversationId === convo.id
							const isNew = newConversationIds.has(convo.id)
							const unreadCount = unreadCounts[convo.id] || 0
							const hasUnread = unreadCount > 0

							return (
								<button
									key={convo.id}
									onClick={() => onSelectConversation(convo.id)}
									className={cn(
										// Base styles
										'w-full p-4 rounded-xl border transition-all duration-200',
										'text-left cursor-pointer group',

										// Default state
										'bg-background border-border',

										// Hover state
										'hover:shadow-md hover:border-primary/40 hover:bg-primary/5',
										'hover:-translate-y-0.5',

										// Active/Pressed state
										'active:scale-[0.99] active:shadow-none',

										// Focus state
										'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',

										// Selected state
										isSelected && [
											'bg-primary/10 border-primary/50',
											'shadow-sm ring-1 ring-primary/30'
										],

										// Unread state - left border accent
										hasUnread && 'border-l-4 border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20'
									)}
									aria-label={t('conversationList.conversationWith', { name: convo.visitor_name, unreadCount: hasUnread ? unreadCount : undefined })}
									aria-current={isSelected ? 'true' : 'false'}
								>
									<div className="flex items-center gap-3 mb-2">
										<div className="relative flex-shrink-0">
											<Avatar className={cn(
												'h-11 w-11 ring-2 ring-transparent transition-all',
												isSelected && 'ring-primary/30'
											)}>
												<AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/5 text-foreground font-semibold text-sm">
													{convo.visitor_name?.[0]?.toUpperCase() || '?'}
												</AvatarFallback>
											</Avatar>
										</div>

										<div className="flex-1 min-w-0">
											<h3 className={cn(
												'text-base truncate',
												hasUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/90'
											)}>
												{convo.visitor_name}
											</h3>
										</div>

										<div className="flex items-center gap-2 flex-shrink-0">
											<span className="text-xs font-medium text-muted-foreground">
												{getCompactTime(convo.last_message_at, t)}
											</span>

											{hasUnread && (
												<Badge className="h-5 min-w-[20px] px-1.5 bg-blue-500 text-white text-xs font-bold border-0 rounded-md">
													{unreadCount > 99 ? '99+' : unreadCount}
												</Badge>
											)}
										</div>
									</div>

									<div className="flex items-start gap-2 mb-2">
										<p className={cn(
											'text-sm leading-tight line-clamp-1 flex-1',
											hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'
										)}>
											{convo.last_message_content || (
												<span className="italic text-muted-foreground/50">{t('conversationList.noMessagesYet')}</span>
											)}
										</p>
									</div>

									<div className="flex items-center gap-2 text-xs">
										<span className="text-muted-foreground/60 truncate max-w-[140px]">
											{convo.visitor_email}
										</span>
										<span className="text-muted-foreground/40">•</span>

										{convo.assigned_agent ? (
											<div className="flex items-center gap-1.5">
												<AgentAvatar
													agent={convo.assigned_agent}
													size="xs"
													showStatus={false}
												/>
												<span className="text-muted-foreground/70 truncate max-w-[80px]">
													{convo.assigned_agent.name || convo.assigned_agent.email}
												</span>
											</div>
										) : (
											<div className="flex items-center gap-1.5">
												<span className="text-orange-600 dark:text-orange-400 font-medium">
													{t('conversationList.unassigned')}
												</span>
												{convo.status === 'open' && (
													<Badge className="h-4 px-1.5 bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-500/20 text-[10px] font-semibold">
														{t('conversationList.new')}
													</Badge>
												)}
											</div>
										)}
									</div>
								</button>
							)
						})}
					</div>
				)}
			</div>
		</div>
	)
}
